import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { GalleryEvent, GalleryImage, ChoirFilterOption } from '../../types/Gallery';
import { Choir } from '../../types/Choir';
import GalleryService from '../../services/GalleryService';
import ChoirService from '../../services/ChoirService';
import { Calendar, MapPin, Users, Image as ImageIcon, ChevronLeft, ChevronRight, Camera, Expand, X } from 'lucide-react';

// Lightbox Modal Component
interface LightboxProps {
  images: GalleryImage[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (index: number) => void;
  eventTitle: string;
}

const Lightbox: React.FC<LightboxProps> = ({
  images,
  currentIndex,
  isOpen,
  onClose,
  onNavigate,
  eventTitle
}) => {
  const getImageDataUrl = (image: GalleryImage): string => {
    // Prefer S3 URL if available (for S3-stored images)
    if (image.s3Url && image.s3Url.trim() !== '') {
      return image.s3Url;
    }
    // Fallback to base64 data for backward compatibility
    if (image.base64Data && image.base64Data.trim() !== '') {
      return `data:${image.contentType};base64,${image.base64Data}`;
    }
    // If neither is available, return empty string (will trigger onError handler)
    return '';
  };

  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    onNavigate(newIndex);
  };

  const goToNext = () => {
    const newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    onNavigate(newIndex);
  };

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, currentIndex]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Prevent any mouse leave events from affecting the lightbox
      document.body.style.pointerEvents = 'auto';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.pointerEvents = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.pointerEvents = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black bg-opacity-90 flex items-center justify-center"
      style={{ pointerEvents: 'auto' }}
      onMouseLeave={(e) => {
        // Prevent any mouse leave events from closing the lightbox
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-[10000] bg-black/50 hover:bg-black/70 text-white rounded-full p-3 transition-colors duration-200"
        aria-label="Close lightbox"
      >
        <X size={24} />
      </button>

      {/* Main image container */}
      <div className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center">
        <img
          src={getImageDataUrl(images[currentIndex])}
          alt={`${eventTitle} - Image ${currentIndex + 1}`}
          className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />

        {/* Navigation arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 transition-colors duration-200"
              aria-label="Previous image"
            >
              <ChevronLeft size={28} />
            </button>

            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 transition-colors duration-200"
              aria-label="Next image"
            >
              <ChevronRight size={28} />
            </button>
          </>
        )}
      </div>

      {/* Image info and thumbnail navigation */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-center">
        {/* Image counter and title */}
        <div className="bg-black/70 text-white px-4 py-2 rounded-lg mb-4">
          <p className="text-sm font-medium">{eventTitle}</p>
          <p className="text-xs opacity-75">
            {currentIndex + 1} of {images.length}
            {images[currentIndex].caption && ` • ${images[currentIndex].caption}`}
          </p>
        </div>

        {/* Thumbnail navigation */}
        {images.length > 1 && (
          <div className="flex gap-2 justify-center max-w-lg overflow-x-auto">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={() => onNavigate(index)}
                className={`flex-shrink-0 w-12 h-12 rounded border-2 transition-all duration-200 ${
                  index === currentIndex
                    ? 'border-white scale-110'
                    : 'border-gray-400 opacity-70 hover:opacity-100 hover:border-white'
                }`}
              >
                <img
                  src={getImageDataUrl(image)}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover rounded"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

// Carousel component for gallery images
interface ImageCarouselProps {
  images: GalleryImage[];
  eventTitle: string;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({ images, eventTitle }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const getImageDataUrl = (image: GalleryImage): string => {
    // Prefer S3 URL if available (for S3-stored images)
    if (image.s3Url && image.s3Url.trim() !== '') {
      return image.s3Url;
    }
    // Fallback to base64 data for backward compatibility
    if (image.base64Data && image.base64Data.trim() !== '') {
      return `data:${image.contentType};base64,${image.base64Data}`;
    }
    // If neither is available, return empty string (will trigger onError handler)
    return '';
  };

  const goToPrevious = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToNext = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const openLightbox = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  const handleLightboxNavigate = (index: number) => {
    setCurrentIndex(index);
  };

  const handleDotClick = (index: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex(index);
  };

  if (images.length === 0) {
    return (
      <div className="w-full h-64 bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
        <Camera size={48} className="text-white opacity-70" />
      </div>
    );
  }

  if (images.length === 1) {
    return (
      <>
        <div className="w-full h-64 relative overflow-hidden rounded-lg group">
          <img
            src={getImageDataUrl(images[0])}
            alt={eventTitle}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />

          {/* Expand indicator - separate clickable area */}
          <div
            className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center cursor-pointer"
            onClick={openLightbox}
          >
            <div className="bg-black/50 text-white rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform scale-90 group-hover:scale-100 pointer-events-none">
              <Expand size={24} />
            </div>
          </div>
        </div>

        <Lightbox
          images={images}
          currentIndex={currentIndex}
          isOpen={isLightboxOpen}
          onClose={closeLightbox}
          onNavigate={handleLightboxNavigate}
          eventTitle={eventTitle}
        />
      </>
    );
  }

  return (
    <>
      <div className={`relative w-full h-64 rounded-lg overflow-hidden ${isLightboxOpen ? '' : 'group'}`}>
        {/* Main image area - clickable for lightbox */}
        <div
          className="w-full h-full cursor-pointer relative"
          onClick={openLightbox}
        >
          <img
            src={getImageDataUrl(images[currentIndex])}
            alt={`${eventTitle} - Image ${currentIndex + 1}`}
            className={`w-full h-full object-cover transition-all duration-300 ${isLightboxOpen ? '' : 'group-hover:scale-105'}`}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />

          {/* Expand indicator - center overlay */}
          <div className={`absolute inset-0 bg-black/0 transition-all duration-300 flex items-center justify-center pointer-events-none ${isLightboxOpen ? '' : 'group-hover:bg-black/20'}`}>
            <div className={`bg-black/50 text-white rounded-full p-3 transition-all duration-300 transform scale-90 ${isLightboxOpen ? 'opacity-0' : 'opacity-0 group-hover:opacity-100 group-hover:scale-100'}`}>
              <Expand size={24} />
            </div>
          </div>
        </div>

        {/* Navigation buttons - only show when lightbox is closed */}
        {!isLightboxOpen && (
          <>
            <button
              onClick={(e) => goToPrevious(e)}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20"
              aria-label="Previous image"
            >
              <ChevronLeft size={20} />
            </button>

            <button
              onClick={(e) => goToNext(e)}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20"
              aria-label="Next image"
            >
              <ChevronRight size={20} />
            </button>

            {/* Dots indicator */}
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => handleDotClick(index, e)}
                  className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                    index === currentIndex ? 'bg-white' : 'bg-white/50'
                  }`}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>

            {/* Image counter */}
            <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs pointer-events-none">
              {currentIndex + 1} / {images.length}
            </div>
          </>
        )}
      </div>

      <Lightbox
        images={images}
        currentIndex={currentIndex}
        isOpen={isLightboxOpen}
        onClose={closeLightbox}
        onNavigate={handleLightboxNavigate}
        eventTitle={eventTitle}
      />
    </>
  );
};

const GalleryEventDisplay: React.FC = () => {
  const [galleryEvents, setGalleryEvents] = useState<GalleryEvent[]>([]);
  const [choirs, setChoirs] = useState<Choir[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [choirFilter, setChoirFilter] = useState('all');
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [filteredEvents, setFilteredEvents] = useState<GalleryEvent[]>([]);

  // Choir type color mapping for tags
  const getChoirColor = (choirName: string): string => {
    const colorMap: { [key: string]: string } = {
      "Women's Choir": 'bg-pink-500',
      "Men's Choir": 'bg-blue-600',
      "Show Choir": 'bg-purple-500',
      "Church Choir": 'bg-green-500',
      "Mixed Choir": 'bg-orange-500',
      "Concert Choir": 'bg-indigo-500'
    };

    // Check for partial matches
    for (const [key, color] of Object.entries(colorMap)) {
      if (choirName.toLowerCase().includes(key.toLowerCase().replace("'s", ""))) {
        return color;
      }
    }

    return 'bg-gray-500'; // Default color
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [eventsData, choirsData] = await Promise.all([
          GalleryService.getPublicGalleryEvents(),
          ChoirService.getAllActiveChoirs()
        ]);
        setGalleryEvents(eventsData);
        setChoirs(choirsData);
        setFilteredEvents(eventsData);
      } catch (err) {
        console.error('Failed to load gallery events:', err);
        setError('Failed to load gallery events');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Get choir filter options
  const getChoirFilterOptions = (): ChoirFilterOption[] => {
    const choirOptions = choirs.map(choir => ({
      id: choir.id,
      name: choir.name,
      color: getChoirColor(choir.name)
    }));

    return [
      { id: 'all', name: 'All Choirs', color: 'bg-gray-500' },
      ...choirOptions
    ];
  };

  // Get event type filter options
  const getEventTypeFilters = (): string[] => {
    return ['all', 'Concert', 'Competition', 'Fundraiser', 'Community Event', 'Rehearsal', 'Other'];
  };

  // Filter events based on both filters
  useEffect(() => {
    let filtered = galleryEvents;

    // Apply choir filter
    if (choirFilter !== 'all') {
      filtered = filtered.filter(event =>
        event.participatingChoirs.includes(choirFilter)
      );
    }

    // Apply event type filter
    if (eventTypeFilter !== 'all') {
      filtered = filtered.filter(event => event.eventType === eventTypeFilter);
    }

    setFilteredEvents(filtered);
  }, [choirFilter, eventTypeFilter, galleryEvents]);

  const getChoirName = (choirId: string): string => {
    const choir = choirs.find(c => c.id === choirId);
    return choir ? choir.name : 'Unknown Choir';
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getEventTypeLabel = (filter: string): string => {
    if (filter === 'all') return 'All Events';
    return filter;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-lg">Loading gallery events...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
        <div className="text-center text-white">
          <p className="text-lg">{error}</p>
        </div>
      </div>
    );
  }

  if (galleryEvents.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-8 py-8">
        <div className="bg-white rounded-2xl p-8 shadow-lg">
          <h2 className="text-3xl font-bold text-secondary-800 mb-6 border-b-4 border-primary-500 pb-2">
            Event Gallery
          </h2>
          <div className="text-center py-12">
            <Camera size={64} className="mx-auto text-gray-400 mb-6" />
            <h3 className="text-2xl font-semibold text-secondary-800 mb-4">No Gallery Events Available</h3>
            <p className="text-secondary-600 text-lg">Check back later for photos from our events.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-8 py-8">
      <div className="bg-white rounded-2xl p-8 shadow-lg">
        {/* Header */}
        <h2 className="text-3xl font-bold text-secondary-800 mb-6 border-b-4 border-primary-500 pb-2">
          Event Gallery
        </h2>

        {/* Dual Filter System */}
        <div className="mb-8 space-y-6">
          {/* Choir Filters */}
          <div>
            <h3 className="text-lg font-semibold text-secondary-700 mb-3">Filter by Choir</h3>
            <div className="flex justify-center gap-3 flex-wrap">
              {getChoirFilterOptions().map((option) => (
                <button
                  key={option.id}
                  onClick={() => setChoirFilter(option.id)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 flex items-center gap-2 ${
                    choirFilter === option.id
                      ? 'bg-primary-500 text-white shadow-lg transform -translate-y-1'
                      : 'bg-primary-50 text-primary-600 border-2 border-primary-200 hover:bg-primary-500 hover:text-white hover:transform hover:-translate-y-1'
                  }`}
                >
                  <div className={`w-3 h-3 rounded-full ${option.color}`}></div>
                  {option.name}
                </button>
              ))}
            </div>
          </div>

          {/* Event Type Filters */}
          <div>
            <h3 className="text-lg font-semibold text-secondary-700 mb-3">Filter by Event Type</h3>
            <div className="flex justify-center gap-3 flex-wrap">
              {getEventTypeFilters().map((filter) => (
                <button
                  key={filter}
                  onClick={() => setEventTypeFilter(filter)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                    eventTypeFilter === filter
                      ? 'bg-secondary-600 text-white shadow-lg transform -translate-y-1'
                      : 'bg-secondary-50 text-secondary-600 border-2 border-secondary-200 hover:bg-secondary-600 hover:text-white hover:transform hover:-translate-y-1'
                  }`}
                >
                  {getEventTypeLabel(filter)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-6 text-center">
          <p className="text-secondary-600">
            Showing {filteredEvents.length} of {galleryEvents.length} events
          </p>
        </div>

        {/* No Results State */}
        {filteredEvents.length === 0 && (
          <div className="text-center py-12">
            <Camera size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-secondary-800 mb-2">No Events Match Your Filters</h3>
            <p className="text-secondary-600">Try adjusting your filter selections to see more events.</p>
          </div>
        )}

        {/* Events Grid */}
        {filteredEvents.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEvents.map((event) => (
              <div
                key={event.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
              >
                {/* Event Image */}
                <ImageCarousel images={event.images} eventTitle={event.title} />

                {/* Event Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-secondary-800 mb-3 line-clamp-2">
                    {event.title}
                  </h3>

                  {/* Event Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-secondary-600">
                      <Calendar size={16} />
                      <span className="text-sm">{formatDate(event.date)}</span>
                    </div>

                    {event.location && (
                      <div className="flex items-center gap-2 text-secondary-600">
                        <MapPin size={16} />
                        <span className="text-sm">{event.location}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-secondary-600">
                      <ImageIcon size={16} />
                      <span className="text-sm">{event.images.length} photos</span>
                    </div>
                  </div>

                  {/* Event Type Tag */}
                  <div className="mb-3">
                    <span className="bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full text-sm font-medium">
                      {event.eventType}
                    </span>
                  </div>

                  {/* Participating Choirs */}
                  {event.participatingChoirs.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-1 text-secondary-600">
                        <Users size={16} />
                        <span className="text-sm font-medium">Participating Choirs:</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {event.participatingChoirs.map((choirId) => {
                          const choirName = getChoirName(choirId);
                          return (
                            <span
                              key={choirId}
                              className={`${getChoirColor(choirName)} text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1`}
                            >
                              <div className="w-2 h-2 rounded-full bg-white/30"></div>
                              {choirName}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Description (if available) */}
                  {event.description && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-secondary-600 text-sm line-clamp-3">
                        {event.description}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryEventDisplay;
